import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill } from "@/components/StatusPill";
import { Download, Plus, DollarSign } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function DispatcherInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [genFor, setGenFor] = useState("");
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/invoices").then((r) => setInvoices(r.data));
    api.get("/facilities").then((r) => setFacilities(r.data));
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!genFor) return;
    try {
      await api.post("/invoices/generate", { facility_id: genFor });
      toast.success("Invoice generated");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const markPaid = async (id) => {
    try { await api.post(`/invoices/${id}/mark-paid`); toast.success("Marked paid"); load(); } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="dispatcher-invoices">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Invoices</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-brand-navy hover:bg-brand-blue" data-testid="disp-gen-invoice-btn"><Plus className="w-4 h-4 mr-1" />Generate invoice</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Generate invoice from approved timesheets</DialogTitle></DialogHeader>
            <Select value={genFor} onValueChange={setGenFor}>
              <SelectTrigger data-testid="gen-invoice-facility-select"><SelectValue placeholder="Select facility" /></SelectTrigger>
              <SelectContent>
                {facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <DialogFooter><Button onClick={generate} className="bg-brand-navy hover:bg-brand-blue" data-testid="gen-invoice-confirm">Generate</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="grid gap-3">
        {invoices.map((inv) => (
          <Card key={inv.id} data-testid={`disp-invoice-${inv.id}`}>
            <CardContent className="p-5 grid grid-cols-12 gap-3 items-center">
              <div className="col-span-12 md:col-span-3">
                <div className="font-bold text-brand-navy">{inv.invoice_number}</div>
                <div className="text-xs text-slate-500">{inv.facility?.name}</div>
              </div>
              <div className="col-span-6 md:col-span-3 text-sm text-slate-600">Due {inv.due_date}</div>
              <div className="col-span-6 md:col-span-2 text-lg font-bold text-brand-navy">${inv.total.toFixed(2)}</div>
              <div className="col-span-6 md:col-span-2"><StatusPill status={inv.status} /></div>
              <div className="col-span-6 md:col-span-2 flex gap-2 md:justify-end">
                <a href={`${API}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm"><Download className="w-4 h-4" /></Button>
                </a>
                {inv.status !== "paid" && (
                  <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => markPaid(inv.id)} data-testid={`mark-paid-${inv.id}`}>
                    <DollarSign className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
